// src/views/menage/CartePlanning.jsx — LA CARTE DE LA JOURNÉE, d'après la
// maquette NEST v677 (`carteRun` et la journée restructurée du 28/10).
//
// Une seule carte pour tout ce que l'agent a à faire ; trois habits :
//   · `grand`  : le PROCHAIN, cadre vert, tout est ouvert ;
//   · `plie`   : ENSUITE, un en-tête qui s'ouvre au toucher ;
//   · `fini`   : FAIT, un en-tête, et « Voir la checklist » à droite.
// Le contenu (phrase, adresse, code, boutons) vient de celui qui l'appelle.

import { useState } from 'react'
import { ChevronDown, Navigation, KeyRound, AlertTriangle, X } from 'lucide-react'
import useEchapPourFermer from '../../lib/useEchapPourFermer'
import useVoileEcran from '../../lib/useVoileEcran'
import { NEST, POLICE } from './palette-nest'

const CIBLE = 44

export function CartePlanning({ testId, habit = 'grand', heure, titre, sousTitre, teinte, droite, ouverteAuDepart = false, children }) {
  const [ouverte, setOuverte] = useState(ouverteAuDepart)
  const grand = habit === 'grand'
  const fini = habit === 'fini'
  const repliable = habit === 'plie'
  const visible = grand || (repliable && ouverte)
  const pastille = teinte === 'aub'
    ? { background: NEST.aubPale, color: NEST.aubTexte }
    : { background: NEST.grisPale, color: NEST.grisFonce }

  const entete = (
    <>
      {heure && (
        <span data-testid={testId ? `${testId}-heure` : undefined}
          style={grand
            ? { fontSize: 22, fontWeight: 700, color: NEST.vertFonce, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }
            : { ...pastille, fontSize: 13, fontWeight: 700, borderRadius: 8, padding: '5px 9px', minWidth: 52,
                maxWidth: 76, textAlign: 'center', flexShrink: 0 }}>
          {heure}
        </span>
      )}
      <span style={{ flex: '1 1 auto', minWidth: 0, textAlign: 'left' }}>
        <span data-testid={testId ? `${testId}-titre` : undefined}
          style={{ display: 'block', fontSize: grand ? 18 : 14.5, fontWeight: 600, color: fini ? NEST.grisFonce : NEST.encre, lineHeight: 1.3 }}>
          {titre}
        </span>
        {sousTitre && (
          <span style={{ display: 'block', fontSize: 12, color: NEST.gris, fontWeight: 500, lineHeight: 1.35 }}>{sousTitre}</span>
        )}
      </span>
      {droite}
      {repliable && (
        <ChevronDown size={20} strokeWidth={2} color={NEST.gris} aria-hidden="true"
          style={{ flexShrink: 0, transform: ouverte ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
      )}
    </>
  )
  const styleEntete = { display: 'flex', alignItems: 'center', gap: 10, width: '100%', fontFamily: POLICE,
    background: fini ? NEST.fondFini : '#fff', border: 'none', padding: grand ? '14px 16px' : '12px 14px',
    borderBottom: grand ? '1px solid #F0F3F1' : 'none' }

  return (
    <article data-testid={testId} data-habit={habit} data-ouverte={visible ? 'oui' : 'non'}
      style={{ background: fini ? NEST.fondFini : '#fff', overflow: 'hidden', borderRadius: grand ? 16 : 14,
        border: grand ? `2px solid ${NEST.vert}` : `1px solid ${NEST.bordCarte}`,
        boxShadow: grand ? '0 4px 18px rgba(15,122,100,.12)' : 'none', fontFamily: POLICE, color: NEST.encre }}>
      {repliable ? (
        <button type="button" aria-expanded={ouverte} onClick={() => setOuverte((o) => !o)}
          style={{ ...styleEntete, cursor: 'pointer', minHeight: CIBLE }}>{entete}</button>
      ) : <div style={styleEntete}>{entete}</div>}
      {visible && children && (
        <div style={{ padding: grand ? '14px 16px 16px' : '12px 14px 14px', display: 'flex', flexDirection: 'column',
          gap: 12, borderTop: repliable ? '1px solid #F0F3F1' : 'none' }}>
          {children}
        </div>
      )}
    </article>
  )
}

/** La phrase de la carte : ce qu'il faut savoir, trait vert à gauche. */
export function PhraseCarte({ children, testId }) {
  return (
    <div data-testid={testId} style={{ background: NEST.fondCarte, borderLeft: `3px solid ${NEST.vert}`,
      borderRadius: '0 10px 10px 0', padding: '9px 11px', fontSize: 14, lineHeight: 1.45 }}>
      {children}
    </div>
  )
}

/** L'adresse, le bouton d'itinéraire et le code de la boîte, sur une ligne. */
export function LigneAcces({ adresse, cp, ville, code, testIdGps, testIdCode }) {
  const lieu = [adresse, cp, ville].filter(Boolean).join(' ')
  if (!adresse && !code) return null
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, fontSize: 13 }}>
      {adresse && <span style={{ flex: '1 1 180px', minWidth: 0, lineHeight: 1.45 }}>{ville ? `${ville} · ${adresse}` : adresse}</span>}
      {adresse && (
        <a data-testid={testIdGps} href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(lieu)}`}
          target="_blank" rel="noopener noreferrer" aria-label="Ouvrir l'itinéraire" title="Ouvrir l'itinéraire"
          style={{ width: 46, height: 46, borderRadius: 11, background: '#fff', border: `1px solid ${NEST.bord}`,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: NEST.encre, flexShrink: 0 }}>
          <Navigation size={18} strokeWidth={2} />
        </a>
      )}
      {code && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginLeft: 'auto', padding: '4px 10px',
          borderRadius: 9, background: NEST.fondCarte }}>
          <KeyRound size={18} strokeWidth={2} color={NEST.grisFonce} aria-label="Boîte à clés" />
          <b data-testid={testIdCode} style={{ fontSize: 26, fontWeight: 700, letterSpacing: '.18em', fontVariantNumeric: 'tabular-nums' }}>
            {code}
          </b>
        </span>
      )}
    </div>
  )
}

/** Le bouton principal d'une carte, noir et pleine largeur. */
export function BoutonCarte({ children, onClick, testId, disabled, grise, ...reste }) {
  return (
    <button type="button" data-testid={testId} onClick={onClick} disabled={disabled} {...reste}
      style={{ width: '100%', minHeight: 56, borderRadius: 14, border: 'none', fontFamily: POLICE,
        background: grise ? NEST.grisPale : NEST.encre, color: grise ? NEST.gris : '#fff',
        fontSize: 16, fontWeight: 700, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? .6 : 1 }}>
      {children}
    </button>
  )
}

/** « Signaler un incident » : partout où l'agent est sur place. */
export function BoutonIncident({ onClick, testId }) {
  return (
    <button type="button" data-testid={testId} onClick={onClick}
      style={{ width: '100%', minHeight: CIBLE, borderRadius: 11, background: '#fff', fontFamily: POLICE,
        border: '1px solid #D9B9D0', color: NEST.aubTexte, fontSize: 13, fontWeight: 600, cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
      <AlertTriangle size={15} strokeWidth={2} /> Signaler un incident
    </button>
  )
}

/** Un lien « Voir la checklist », à droite d'une carte finie. */
export function LienCarte({ children, onClick, testId, ...reste }) {
  return (
    <button type="button" data-testid={testId} onClick={onClick} {...reste}
      style={{ background: 'none', border: 'none', color: NEST.vertFonce, fontFamily: POLICE, fontWeight: 700,
        fontSize: 13, cursor: 'pointer', flexShrink: 0, minHeight: CIBLE, padding: '0 2px', whiteSpace: 'nowrap' }}>
      {children}
    </button>
  )
}

/** Une feuille : un titre, ✕, le contenu. Voile, Échap ou ✕ pour refermer. */
export function FeuilleAgent({ titre, onFermer, testId, children }) {
  useEchapPourFermer(onFermer)
  useVoileEcran()
  return (
    <div onClick={onFermer} style={{ position: 'fixed', inset: 0, zIndex: 998, background: 'rgba(23,33,30,.45)',
      display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 12 }}>
      <div data-testid={testId} role="dialog" aria-modal="true" aria-label={titre} onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 520, maxHeight: '88vh', overflowY: 'auto',
          padding: 16, fontFamily: POLICE, color: NEST.encre }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <b style={{ flex: 1, fontSize: 16, fontWeight: 700 }}>{titre}</b>
          <button type="button" onClick={onFermer} aria-label="Fermer" title="Fermer"
            style={{ width: CIBLE, height: CIBLE, border: 'none', background: 'none', cursor: 'pointer', color: NEST.encre }}>
            <X size={20} strokeWidth={2} />
          </button>
        </div>
        {children}
      </div>
    </div>
  )
}
